import store from '@/store';
import { confirmEmail } from '@/services/api';
import { Navigation } from '@/types';
import router from './index';
import { objectIsEmpty } from '@/utils';

// lazy loading components with dynamic imports
const About = () => import('@/views/About');
const AboutComplex = () => import('@/components/AboutComplex');
const AboutDesign = () => import('@/components/AboutDesign');
const AboutServices = () => import('@/components/AboutServices');
const Accounts = () => import('@/views/Accounts');
const AppFooter = () => import('@/components/AppFooter');
const CardPage = () => import('@/components/CardPage');
const Catalog = () => import('@/views/Catalog');
const ConfirmByPhone = () => import('@/components/ConfirmByPhone');
const EditPassword = () => import('@/components/EditPassword');
const EditPersonalData = () => import('@/components/EditPersonalData');
const FAQ = () => import('@/components/FAQ');
const HowItWorks = () => import('@/components/HowItWorks');
const LoginForm = () => import('@/components/LoginForm');
const MainPage = () => import('@/views/MainPage');
const MessageBox = () => import('@/components/MessageBox');
const MessageEmailFailed = () => import('@/components/Messages/MessageEmailFailed');
const MessageEmailSuccess = () => import('@/components/Messages/MessageEmailSuccess');
const MessageConfirmPhone = () => import('@/components/Messages/MessageConfirmPhone');
const VerifyEmail = () => import('@/components/VerifyEmail');
const News = () => import('@/views/News');
const NewsItem = () => import('@/components/NewsItem');
const NewsList = () => import('@/components/NewsList');
const PageNotFound = () => import('@/components/PageNotFound');
const PasswordResetSuccess = () => import('@/components/Messages/PasswordResetSuccsess');
const Profile = () => import('@/views/Profile');
const ProfileApartmentOne = () => import('@/components/ProfileApartmentOne');
const ProfileApartmentOneAbout = () => import('@/components/ProfileApartmentOneAbout');
const ProfileApartmentOnePayments = () => import('@/components/ProfileApartmentOnePayments');
const ProfileApartments = () => import('@/components/ProfileApartments');
const ProfileDashboard = () => import('@/components/ProfileDashboard');
const ProfileEdit = () => import('@/components/ProfileEdit');
const ProfileNotifications = () => import('@/components/ProfileNotifications');
const ProfilePayments = () => import('@/components/ProfilePayments');
const ProfilePaymentsCommunal = () => import('@/components/ProfilePaymentsCommunal');
const ProfilePaymentsRent = () => import('@/components/ProfilePaymentsRent');
const ProfilePaymentsServices = () => import('@/components/ProfilePaymentsServices');
const ProfileServices = () => import('@/components/ProfileServices');
const ProfileSupport = () => import('@/components/ProfileSupport');
const RecoveryPassForm = () => import('@/components/RecoveryPassForm');
const RecoveryPassInfo = () => import('@/components/RecoveryPassInfo');
const Reservation = () => import('@/views/Reservation');
const SetPasswordForm = () => import('@/components/SetPassForm');
const SignUpForm = () => import('@/components/SignUpForm');
const SupportCreated = () => import('@/components/SupportCreated');
const SupportList = () => import('@/components/SupportList');
const SupportOpened = () => import('@/components/SupportOpened');
const ToBusiness = () => import('@/views/ToBusiness');
const ToRenters = () => import('@/views/ToRenters');
const VerifyLodger = () => import('@/views/VerifyLodger');
const ContinueEndingRent = () => import('@/components/ContinueEndingRent');

export const config: Navigation[] = [
  {
    path: '/',
    name: 'main',
    components: {
      default: MainPage,
      footer: AppFooter,
    },
    props: {
      fragments: {
          banner: 'banner',
      },
    },
  },
  {
    path: '/about',
    name: 'about',
    components: {
      default: About,
      footer: AppFooter,
    },
    redirect: { name: 'aboutDesign' },
    children: [
      {
        name: 'aboutDesign',
        path: 'about-design',
        component: AboutDesign,
      },
      {
        name: 'aboutComplex',
        path: 'about-complex',
        component: AboutComplex,
      },
      {
        name: 'aboutServices',
        path: 'about-services',
        component: AboutServices,
      },
    ],
  },
  {
    path: '/to-renters',
    name: 'toRenters',
    components: {
      default: ToRenters,
      footer: AppFooter,
    },
    redirect: { name: 'HIWRenters' },
    children: [
      {
        name: 'HIWRenters',
        path: 'how-it-works',
        component: HowItWorks,
      },
      {
        name: 'faqRenters',
        path: 'faq',
        component: FAQ,
      },
    ],
  },
  {
    path: '/to-business',
    name: 'toBusiness',
    components: {
      default: ToBusiness,
      footer: AppFooter,
    },
  },
  {
    path: '/news',
    name: 'news',
    components: {
      default: News,
      footer: AppFooter,
    },
    redirect: {name: 'newsList'},
    children: [
      {
        path: 'all-news',
        name: 'newsList',
        component: NewsList,
      },
      {
        path: ':id',
        name: 'newsItem',
        component: NewsItem,
        props: (route) => ({
          id: route.params.id,
        }),
      },
    ],
  },
  {
    path: '/catalog/',
    name: 'catalog',
    components: {
      default: Catalog,
      footer: AppFooter,
    },
  },
  {
    path: '/catalog/:design-:rooms-:id',
    name: 'reservation',
    components: {
      default: Reservation,
      footer: AppFooter,
    },
    props: { default: true, AppFooter: false },
  },
  {
    path: '/accounts',
    name: 'accounts',
    components: {
      default: Accounts,
    },
    redirect: { name: 'login' },
    children: [
      {
        path: 'confirm-2fa',
        name: 'confirm2fa',
        component: ConfirmByPhone,
        props: true,
      },
      {
        path: 'login',
        name: 'login',
        component: LoginForm,
        beforeEnter: (to, from, next) => {
          if (store.getters['user/logged']) {
            next('/');
          } else {
            next();
          }
        },
      },
      {
        path: 'sign-up',
        name: 'signUp',
        component: SignUpForm,
      },
      {
        path: 'confirm',
        name: 'confirm',
        component: ConfirmByPhone,
        props: true,
      },
      {
        path: 'pass-recover',
        name: 'passRecover',
        component: RecoveryPassForm,
      },
      {
        path: 'password-reset',
        name: 'setPassword',
        component: SetPasswordForm,
        props: (route) => ({
          id: route.query.id,
          token: route.query.token,
        }),
      },
      {
        path: 'message-send-mail',
        name: 'messageSendMail',
        component: RecoveryPassInfo,
        props: true,
      },
      {
        path: 'message-confirm-phone',
        name: 'messageConfirmPhone',
        component: MessageConfirmPhone,
        beforeEnter: (to, from, next) => {
          if (objectIsEmpty(to.params)) {
            next('/page-not-found');
          } else {
            next();
          }
        },
      },
      {
        path: 'message-email-success',
        name: 'messageEmailSuccess',
        component: MessageEmailSuccess,
        beforeEnter: (to, from, next) => {
          if (objectIsEmpty(to.params)) {
            next('/page-not-found');
          } else {
            next();
          }
        },
      },
      {
        path: 'message-email-failed',
        name: 'messageEmailFailed',
        component: MessageEmailFailed,
        beforeEnter: (to, from, next) => {
          if (objectIsEmpty(to.params)) {
            next('/page-not-found');
          } else {
            next();
          }
        },
      },
      {
        path: 'password-reset-success',
        name: 'passwordResetSuccess',
        component: PasswordResetSuccess,
        beforeEnter: (to, from, next) => {
          if (objectIsEmpty(to.params)) {
            next('/page-not-found');
          } else {
            next();
          }
        },
      },
      {
        path: 'registration-confirm-email/:code',
        name: 'confirmAccountByEmail',
        beforeEnter: async (to, from, next) => {
          await store.dispatch('accounts/fetchTranslation');
          confirmEmail(to.params.code).then((response) => {
            const push = (name: string) => {
              const translations = store.getters['accounts/translations'][name];
              next({name, params: {
                title: translations.title,
                text: translations.message,
              }});
            };
            if (response.status === 200) {
              push('messageEmailSuccess');
            } else {
              push('messageEmailFailed');
            }
          }).catch((error) => console.error(error));
        },
      },
    ],
  },
  {
    path: '/profile',
    name: 'profile',
    components: {
      default: Profile,
    },
    beforeEnter: (to, from, next) => {
      if (store.getters['user/logged']) {
        next();
      } else {
        router.pushAndSave('accounts/login', null, next);
      }
    },
    redirect: { name: 'profileDashboard' },
    children: [
      {
        name: 'profileDashboard',
        path: 'dashboard',
        component: ProfileDashboard,
      },
      {
        name: 'myApartments',
        path: 'my-apartments',
        component: ProfileApartments,
        children: [
          {
            name: 'myApartmentOne',
            path: ':id',
            component: ProfileApartmentOne,
            redirect: { name: 'apartmentAbout' },
            children: [
              {
                name: 'apartmentAbout',
                path: 'about',
                component: ProfileApartmentOneAbout,
              },
              {
                name: 'apartmentPayments',
                path: 'payments-apartment',
                component: ProfileApartmentOnePayments,
              },
              {
                name: 'leaseContractClose',
                path: 'close',
                component: ContinueEndingRent,
              },
              {
                name: 'leaseContractProlong',
                path: 'prolong',
                component: ContinueEndingRent,
              },
            ],
          },
        ],
      },
      {
        name: 'payments',
        path: 'payments',
        component: ProfilePayments,
        redirect: { name: 'paymentsRent' },
        children: [
          {
            name: 'paymentsRent',
            path: 'payments-rent',
            component: ProfilePaymentsRent,
          },
          {
            name: 'paymentsCommunal',
            path: 'payments-communal',
            component: ProfilePaymentsCommunal,
          },
          {
            name: 'paymentsServices',
            path: 'payments-services',
            component: ProfilePaymentsServices,
          },
        ],
      },
      {
        name: 'service',
        path: 'service',
        component: ProfileServices,
      },
      {
        name: 'support',
        path: 'support/',
        component: ProfileSupport,
        redirect: { name: 'supportList' },
        children: [
          {
            name: 'supportList',
            path: 'list/',
            component: SupportList,
          },
          {
            name: 'supportCreate',
            path: 'create/',
            component: SupportCreated,
          },
          {
            name: 'feedback',
            path: ':id',
            component: SupportOpened,
          },
        ],
      },
      {
        name: 'notifications',
        path: 'notifications',
        component: ProfileNotifications,
      },
      {
        name: 'edit',
        path: 'edit',
        component: ProfileEdit,
        redirect: { name: 'personalData' },
        children: [
          {
            name: 'personalData',
            path: 'personal',
            component: EditPersonalData,
          },
          {
            name: 'password',
            path: 'password',
            component: EditPassword,
          },
          {
            name: 'card',
            path: 'card',
            component: CardPage,
            props: (route) => ({ orderNumber: route.query.order_number }),
          },
        ],
      },
    ],
  },
  {
    path: '/page-not-found',
    name: 'pageNotFound',
    components: {
      default: PageNotFound,
    },
  },
  {
    path: '/verify-lodger/:code',
    name: 'verifyLodger',
    beforeEnter: (to, from, next) => {
      if (store.getters['user/logged']) {
        next();
      } else {
        router.pushAndSave('accounts/login', next);
      }
    },
    components: {
      default: VerifyLodger,
    },
  },
  {
    path: '/message',
    name: 'message',
    component: MessageBox,
    props: true,
    beforeEnter: (to, from, next) => {
      if (objectIsEmpty(to.params)) {
        next('/page-not-found');
      } else {
        next();
      }
    },
  },
  // NOTE: the path variables can be chnged from admin
  {
    path: '/verify-email/:id_user/:email_token',
    name: 'verifyEmail',
    component: VerifyEmail,
    props: true,
  },
  {
    path: '*',
    redirect: { name: 'pageNotFound' },
  },
];

export const userIsRequired: any[] = [
  'profile', 'accounts/login', 'verify-lodger',
];
